import React, { useEffect, useState } from "react";
import {
  BarChart3,
  Users,
  ShoppingCart,
  Pill,
  MapPin,
  UserPlus,
  Building2,
  Truck,
  Calendar,
  ArrowUp,
  ArrowDown,
  DollarSign,
  Activity,
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  CheckCircleIcon,
} from "lucide-react";
import { Box, Chip, Skeleton } from "@mui/material";
import ErrorIcon from "@mui/icons-material/Error";
import axios from "axios";
import UserGraph from "./UserGraph";

const getColorClasses = (color) => {
  const colorMap = {
    blue: "bg-blue-500 text-blue-500 bg-blue-50",
    green: "bg-green-500 text-green-500 bg-green-50",
    purple: "bg-purple-500 text-purple-500 bg-purple-50",
    orange: "bg-orange-500 text-orange-500 bg-orange-50",
    pink: "bg-pink-500 text-pink-500 bg-pink-50",
    indigo: "bg-indigo-500 text-indigo-500 bg-indigo-50",
    red: "bg-red-500 text-red-500 bg-red-50",
    teal: "bg-teal-500 text-teal-500 bg-teal-50",
    cyan: "bg-cyan-500 text-cyan-500 bg-cyan-50",
    emerald: "bg-emerald-500 text-emerald-500 bg-emerald-50",
  };
  return colorMap[color] || colorMap.blue;
};

// Calendar Component
const CustomCalendar = ({
  selectedRange,
  onRangeSelect,
  isOpen,
  onClose,
  handlePeriodChange,
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [startDate, setStartDate] = useState(selectedRange?.start || null);
  const [endDate, setEndDate] = useState(selectedRange?.end || null);
  const [isSelectingEnd, setIsSelectingEnd] = useState(false);

  if (!isOpen) return null;

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    for (let day = 1; day <= lastDay.getDate(); day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const isDateInRange = (date) => {
    if (!startDate || !endDate || !date) return false;
    return date >= startDate && date <= endDate;
  };

  const isDateSelected = (date) => {
    if (!date) return false;
    return (
      (startDate && date.toDateString() === startDate.toDateString()) ||
      (endDate && date.toDateString() === endDate.toDateString())
    );
  };

  const handleDateClick = (date) => {
    if (!startDate || (startDate && endDate)) {
      setStartDate(date);
      setEndDate(null);
      setIsSelectingEnd(true);
    } else if (startDate && !endDate) {
      if (date < startDate) {
        setEndDate(startDate);
        setStartDate(date);
      } else {
        setEndDate(date);
      }
      setIsSelectingEnd(false);
    }
  };

  const handleApply = () => {
    if (startDate && endDate) {
      onRangeSelect({ start: new Date(startDate), end: new Date(endDate) });
      handlePeriodChange("custom", startDate, endDate);
      onClose();
    }
  };

  const navigateMonth = (direction) => {
    setCurrentMonth((prev) => {
      const newMonth = new Date(prev);
      newMonth.setMonth(prev.getMonth() + direction);
      return newMonth;
    });
  };

  const days = getDaysInMonth(currentMonth);
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  return (
    <div className="min-w-80 absolute top-full left-0 z-50 mt-2 rounded-lg border border-gray-200 bg-white p-3 shadow-lg">
      <div className="mb-3 flex items-center justify-between">
        <button
          onClick={() => navigateMonth(-1)}
          className="rounded p-1 hover:bg-gray-100"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <h3 className="text-sm font-semibold">
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </h3>
        <button
          onClick={() => navigateMonth(1)}
          className="rounded p-1 hover:bg-gray-100"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      <div className="mb-2 grid grid-cols-7 gap-1">
        {dayNames.map((day) => (
          <div
            key={day}
            className="p-1 text-center text-xs font-medium text-gray-500"
          >
            {day}
          </div>
        ))}
      </div>

      <div className="mb-3 grid grid-cols-7 gap-1">
        {days.map((date, index) => (
          <button
            key={index}
            onClick={() => date && handleDateClick(date)}
            disabled={!date}
            className={`
              rounded p-1.5 text-xs transition-colors
              ${!date ? "invisible" : ""}
              ${isDateSelected(date) ? "bg-blue-500 text-white" : ""}
              ${
                isDateInRange(date) && !isDateSelected(date)
                  ? "bg-blue-100"
                  : ""
              }
              ${
                date && !isDateSelected(date) && !isDateInRange(date)
                  ? "hover:bg-gray-100"
                  : ""
              }
              ${
                date && date.toDateString() === new Date().toDateString()
                  ? "font-bold"
                  : ""
              }
            `}
          >
            {date?.getDate()}
          </button>
        ))}
      </div>

      <div className="flex items-center justify-between border-t pt-2">
        <div className="text-xs text-gray-600">
          {startDate &&
            endDate &&
            `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`}
          {startDate &&
            !endDate &&
            isSelectingEnd &&
            `${startDate.toLocaleDateString()} - Select end date`}
        </div>
        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="rounded px-2 py-1 text-xs text-gray-600 hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            onClick={handleApply}
            disabled={!startDate || !endDate}
            className="rounded bg-blue-500 px-2 py-1 text-xs text-white hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

const AnalyticsPage = () => {
  const [selectedPeriod, setSelectedPeriod] = useState("30");
  const [dateRange, setDateRange] = useState("Nov 19, 2024 → Dec 19, 2024");
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [customDateRange, setCustomDateRange] = useState({
    start: new Date(2024, 10, 19),
    end: new Date(2024, 11, 19),
  });
  const [statsdata, setStatsdata] = useState([]);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [getdurgsandotcproduct, setGetdurgsandOTCProduct] = useState();

  // Icon mapping
  const iconMap = {
    "Total Orders": ShoppingCart,
    "Total Earnings": DollarSign,
    "Total OTC Products": Pill,
    "Total Drugs": Pill,
    "Total Customers": Users,
    "User Signups": UserPlus,
    "Pharmacy SignUps": Building2,
    "Runner SignUps": Truck,
    "User Locations": MapPin,
    "Pharmacy Locations": MapPin,
    "Runner Locations": MapPin,
  };

  // Color mapping
  const getColor = (title) => {
    switch (title) {
      case "Total Orders":
        return "blue";
      case "Total Earnings":
        return "green";
      case "Total Medicines":
        return "purple";
      case "Total Customers":
        return "orange";
      case "User Signups":
        return "pink";
      case "Pharmacy SignUps":
        return "indigo";
      case "Runner SignUps":
        return "red";
      case "User Locations":
        return "teal";
      case "Pharmacy Locations":
        return "cyan";
      case "Runner Locations":
        return "emerald";
      default:
        return "gray";
    }
  };

  const get_durgs_OTC_product = async () => {
    try {
      const res = await axios.get(
        `https://api.medstown.com/api/admin-controls/dashboard/durgs-otc-product`
      );
      const newdata = res.data;
      const updatedStats = newdata.stats.map((stat) => ({
        ...stat,
        icon: iconMap[stat.title] || MapPin,
        color: getColor(stat.title),
      }));
      setGetdurgsandOTCProduct({ stats: updatedStats });
    } catch (error) {
      console.log(error);
    }
  };

  const fetchStatus = async () => {
    try {
      const res = await axios.get(
        "https://api.medstown.com/admin/check-services"
      );

      const grouped = {
        title: "System Status",
        allHealthy: res.data.every(
          (api) => api.status === "UP" || api.status === "OK"
        ),
        apis: res.data
          .filter((api) => api.apiName)
          .map((api) => ({
            name: api.apiName || "Unknown API",
            url: api.url || "",
            status: api.status || "UNKNOWN",
            responseTime: api.responseTimeMs || 0,
            lastChecked: api.lastChecked
              ? new Date(api.lastChecked).toLocaleString()
              : "OK",
            details:
              api.details === "State: 1"
                ? "OK"
                : api.details && api.details.trim() !== ""
                ? api.details
                : "OK",
          })),
      };

      setData([grouped]);
    } catch (error) {
      console.error("Failed to load status:", error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    get_durgs_OTC_product();
  }, []);

  const timeButtons = [
    { label: "Hour wise", value: "hourWise" },
    { label: "Today", value: "today" },
    { label: "Yesterday", value: "yesterday" },
    { label: "This Week", value: "thisWeek" },
    { label: "Last Week", value: "lastWeek" },
    { label: "This Month", value: "thisMonth" },
    { label: "Last Month", value: "lastMonth" },
    { label: "This Year", value: "thisYear" },
    { label: "Last Year", value: "lastYear" },
  ];

  const formatDisplayDate = (date) =>
    date.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });

  const handlePeriodChange = async (
    period,
    customStart = null,
    customEnd = null
  ) => {
    setSelectedPeriod(period);
    setIsCalendarOpen(false);
    setLoading(true);

    const today = new Date();
    let startDate, endDate;

    if (period === "custom" && customStart && customEnd) {
      startDate = new Date(customStart);
      endDate = new Date(customEnd);
    } else {
      switch (period) {
        case "today":
          startDate = new Date(
            Date.UTC(
              today.getUTCFullYear(),
              today.getUTCMonth(),
              today.getUTCDate(),
              0,
              0,
              0
            )
          );
          endDate = new Date(
            Date.UTC(
              today.getUTCFullYear(),
              today.getUTCMonth(),
              today.getUTCDate(),
              23,
              59,
              59,
              999
            )
          );
          break;

        case "yesterday": {
          const yesterday = new Date(today);
          yesterday.setUTCDate(yesterday.getUTCDate() - 1);
          startDate = new Date(
            Date.UTC(
              yesterday.getUTCFullYear(),
              yesterday.getUTCMonth(),
              yesterday.getUTCDate(),
              0,
              0,
              0
            )
          );
          endDate = new Date(
            Date.UTC(
              yesterday.getUTCFullYear(),
              yesterday.getUTCMonth(),
              yesterday.getUTCDate(),
              23,
              59,
              59,
              999
            )
          );
          break;
        }

        case "thisWeek": {
          const day = today.getUTCDay();
          const diff = today.getUTCDate() - day + (day === 0 ? -6 : 1);
          startDate = new Date(
            Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), diff, 0, 0, 0)
          );
          endDate = new Date();
          break;
        }

        case "lastWeek": {
          const prevSunday = new Date(today);
          prevSunday.setUTCDate(
            prevSunday.getUTCDate() - prevSunday.getUTCDay()
          );
          prevSunday.setUTCDate(prevSunday.getUTCDate() - 7);
          startDate = new Date(
            Date.UTC(
              prevSunday.getUTCFullYear(),
              prevSunday.getUTCMonth(),
              prevSunday.getUTCDate(),
              0,
              0,
              0
            )
          );
          endDate = new Date(
            Date.UTC(
              prevSunday.getUTCFullYear(),
              prevSunday.getUTCMonth(),
              prevSunday.getUTCDate() + 6,
              23,
              59,
              59,
              999
            )
          );
          break;
        }

        case "thisMonth":
          startDate = new Date(
            Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1, 0, 0, 0)
          );
          endDate = new Date();
          break;

        case "lastMonth":
          startDate = new Date(
            Date.UTC(
              today.getUTCFullYear(),
              today.getUTCMonth() - 1,
              1,
              0,
              0,
              0
            )
          );
          endDate = new Date(
            Date.UTC(
              today.getUTCFullYear(),
              today.getUTCMonth(),
              0,
              23,
              59,
              59,
              999
            )
          );
          break;

        case "thisYear":
          startDate = new Date(Date.UTC(today.getUTCFullYear(), 0, 1, 0, 0, 0));
          endDate = new Date();
          break;

        case "lastYear":
          startDate = new Date(
            Date.UTC(today.getUTCFullYear() - 1, 0, 1, 0, 0, 0)
          );
          endDate = new Date(
            Date.UTC(today.getUTCFullYear() - 1, 11, 31, 23, 59, 59, 999)
          );
          break;

        case "hourWise":
          startDate = new Date(Date.now() - 3600000);
          endDate = new Date();
          break;

        default:
          return;
      }
    }

    setDateRange(
      `${formatDisplayDate(startDate)} → ${formatDisplayDate(endDate)}`
    );

    const formattedStart = startDate.toISOString();
    const formattedEnd = endDate.toISOString();

    try {
      const res = await fetch(
        `https://api.medstown.com/api/admin-controls/dashboard-stats?startDate=${formattedStart}&endDate=${formattedEnd}`
      );
      const data = await res.json();

      const updatedStats = data.stats.map((stat) => ({
        ...stat,
        icon: iconMap[stat.title] || MapPin,
        color: getColor(stat.title),
      }));

      setStatsdata({ stats: updatedStats });
    } catch (err) {
      console.error("Failed to fetch dashboard stats:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handlePeriodChange("today");
  }, []);

  const handleCustomDateRange = (range) => {
    setCustomDateRange(range);
    setSelectedPeriod("custom");
    setDateRange(
      `${range.start.toLocaleDateString()} → ${range.end.toLocaleDateString()}`
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <UserGraph />

      <div className="p-4">
        {/* Header */}
        <div className="mb-4 mt-8">
          <h3 className="text-l ml-1 mb-1 font-bold text-gray-900">
            Monitor your business performance and key metrics
          </h3>
        </div>

        {/* Date Range Selector */}
        <div className="mb-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="mb-3 flex items-center gap-2">
            <Calendar className="h-4 w-4 text-gray-500" />
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <Box>
                <h2 className="text-sm font-semibold text-gray-900">
                  Date Range
                </h2>
              </Box>
              <Box
                display="flex"
                justifyContent="flex-end"
                flexDirection="column"
                gap={1}
              >
                {data.map((section, index) => (
                  <Box key={index}>
                    {section.allHealthy ? (
                      <Chip
                        icon={<CheckCircleIcon sx={{ color: "#00cc66" }} />}
                        label="All Healthy"
                        size="small"
                        sx={{
                          border: "1px solid #00cc66",
                          color: "#00cc66",
                          backgroundColor: "transparent",
                          fontWeight: 500,
                          height: "24px",
                          fontSize: "11px",
                        }}
                      />
                    ) : (
                      <Chip
                        icon={<ErrorIcon sx={{ color: "#d32f2f" }} />}
                        label="Issues Found"
                        size="small"
                        sx={{
                          border: "1px solid #d32f2f",
                          color: "#d32f2f",
                          backgroundColor: "transparent",
                          fontWeight: 500,
                          height: "24px",
                          fontSize: "11px",
                        }}
                      />
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          </div>

          <div className="mb-3">
            <div className="inline-flex items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5">
              <Activity className="h-3 w-3 text-blue-600" />
              <span className="text-xs font-medium text-blue-900">
                {dateRange}
              </span>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {timeButtons.map((button) => (
              <button
                key={button.value}
                onClick={() => handlePeriodChange(button.value)}
                className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-all duration-200 ${
                  selectedPeriod === button.value
                    ? "bg-blue-500 text-white shadow-md"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-sm"
                }`}
              >
                {button.label}
              </button>
            ))}

            {/* Custom Date Range Button */}
            <div className="relative">
              <button
                onClick={() => setIsCalendarOpen(!isCalendarOpen)}
                className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-all duration-200 ${
                  selectedPeriod === "custom"
                    ? "bg-blue-500 text-white shadow-md"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-sm"
                }`}
              >
                <CalendarDays className="h-3 w-3" />
                Custom Range
              </button>

              <CustomCalendar
                selectedRange={customDateRange}
                onRangeSelect={handleCustomDateRange}
                isOpen={isCalendarOpen}
                onClose={() => setIsCalendarOpen(false)}
                handlePeriodChange={handlePeriodChange}
              />
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {loading
            ? [...Array(10)].map((_, i) => (
                <div
                  key={i}
                  className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm"
                >
                  <div className="mb-2 flex items-start justify-between">
                    <Skeleton variant="circular" width={32} height={32} />
                    <Skeleton variant="rectangular" width={50} height={18} />
                  </div>
                  <Skeleton variant="text" height={24} width="60%" />
                  <Skeleton variant="text" height={16} width="80%" />
                </div>
              ))
            : statsdata?.stats?.map((stat, index) => {
                const IconComponent = stat.icon;
                const colorClasses = getColorClasses(stat.color).split(" ");

                return (
                  <div
                    key={index}
                    className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm transition-shadow duration-200 hover:shadow-md"
                  >
                    <div className="mb-2 flex items-start justify-between">
                      <div className={`rounded-lg p-2 ${colorClasses[2]}`}>
                        <IconComponent
                          className={`h-4 w-4 ${colorClasses[1]}`}
                        />
                      </div>
                      <div
                        className={`flex items-center gap-1 rounded-full px-1.5 py-0.5 text-xs font-medium ${
                          stat.changeType === "increase"
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        {stat.changeType === "increase" ? (
                          <ArrowUp className="h-3 w-3" />
                        ) : (
                          <ArrowDown className="h-3 w-3" />
                        )}
                        {stat.change}
                      </div>
                    </div>

                    <div>
                      <h3 className="mb-0.5 text-xl font-bold text-gray-900">
                        {stat.value}
                      </h3>
                      <p className="text-xs font-medium text-gray-900">
                        {stat.title}
                      </p>
                    </div>
                  </div>
                );
              })}
        </div>

        {/* Drugs and OTC Product Stats */}
        <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {loading
            ? [...Array(2)].map((_, i) => (
                <div
                  key={i}
                  className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm"
                >
                  <div className="mb-2 flex items-start justify-between">
                    <Skeleton variant="circular" width={32} height={32} />
                    <Skeleton variant="rectangular" width={50} height={18} />
                  </div>
                  <Skeleton variant="text" height={24} width="60%" />
                  <Skeleton variant="text" height={16} width="80%" />
                </div>
              ))
            : getdurgsandotcproduct?.stats?.map((stat, index) => {
                const IconComponent = stat.icon;
                const colorClasses = getColorClasses(stat.color).split(" ");

                return (
                  <div
                    key={index}
                    className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm transition-shadow duration-200 hover:shadow-md"
                  >
                    <div className="mb-2 flex items-start justify-between">
                      <div className={`rounded-lg p-2 ${colorClasses[2]}`}>
                        <IconComponent
                          className={`h-4 w-4 ${colorClasses[1]}`}
                        />
                      </div>
                      <div
                        className={`flex items-center gap-1 rounded-full px-1.5 py-0.5 text-xs font-medium ${
                          stat.changeType === "increase"
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        {stat.changeType === "increase" ? (
                          <ArrowUp className="h-3 w-3" />
                        ) : (
                          <ArrowDown className="h-3 w-3" />
                        )}
                        {stat.change}
                      </div>
                    </div>

                    <div>
                      <h3 className="mb-0.5 text-xl font-bold text-gray-900">
                        {stat.value}
                      </h3>
                      <p className="text-xs font-medium text-gray-900">
                        {stat.title}
                      </p>
                    </div>
                  </div>
                );
              })}
        </div>

        {/* Footer */}
        <div className="mt-4 text-center">
          <p className="text-xs text-gray-500">
            Last updated: {new Date().toLocaleString()}
          </p>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsPage;
